import { CheckCircle, Eye, Trash } from "@ignite-analytics/icons";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import * as Sentry from "@sentry/react";
import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";

import useToken from "../../../hooks/useToken";
import { useAlert } from "../../../lib/Alerts";
import { authHeader, formsFetch, unwrap } from "../../../lib/formsApi";
import { ShapeIcon } from "../../ShapeIcon";
import { canPreviewInline, guessContentType } from "../files";
import OverflowTooltip from "../OverflowTooltip";

interface Props {
    fileName: string;
    answerId: string;
    onDeleteFile: (fileName: string) => void;
    disabled?: boolean;
}

export const FileListItem = ({ fileName, onDeleteFile: deleteFile, answerId, disabled = false }: Props) => {
    const { token } = useToken();
    const { alertUser } = useAlert();
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = useCallback(() => {
        if (isDeleting) return;
        setIsDeleting(true);
        deleteFile(fileName);
        setTimeout(() => {
            setIsDeleting(false);
        }, 2000);
    }, [deleteFile, fileName, isDeleting]);

    const downloadFile = async () => {
        try {
            const { url } = unwrap(
                await formsFetch.POST("/api/files/download-url", {
                    headers: authHeader(token),
                    body: { referenceId: answerId, fileName },
                }),
                "create download url"
            );
            const response = await fetch(url, {
                headers: {
                    "Content-Type": guessContentType(fileName),
                    "X-Content-Type-Options": "nosniff",
                },
            });
            if (!response.ok) {
                throw new Error(`Download request failed with status ${response.status}`);
            }
            const blobUrl = window.URL.createObjectURL(await response.blob());
            if (canPreviewInline(fileName)) {
                window.open(blobUrl, "_blank", "noopener,noreferrer");
            } else {
                // window.open on a blob the browser cannot render saves it under the blob
                // URL's random name with no extension, so hand it over as a named download.
                const link = document.createElement("a");
                link.href = blobUrl;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
            }
            setTimeout(() => {
                window.URL.revokeObjectURL(blobUrl);
            }, 10000);
        } catch (downloadError) {
            Sentry.captureException(downloadError, {
                extra: { fileName, answerId, context: "File download error" },
            });
            alertUser({
                value: <FormattedMessage defaultMessage="Could not open the file. Please try again." />,
                severity: "error",
            });
        }
    };

    return (
        <Stack
            component={Paper}
            variant="outlined"
            direction="row"
            alignItems="center"
            alignContent="center"
            gap={1}
            justifyContent="space-between"
            sx={{
                px: 2,
                py: 1.5,
            }}
        >
            <ShapeIcon color="success" size="small">
                <CheckCircle fontSize="inherit" />
            </ShapeIcon>

            {/* File Name */}
            <Box sx={{ flex: 1, overflow: "hidden", display: "flex", alignItems: "center" }}>
                <OverflowTooltip text={fileName} />
            </Box>

            {/* Action Buttons */}
            <Stack direction="row" gap={0.5} alignItems="center">
                <IconButton size="xsmall" onClick={downloadFile} color="secondary">
                    <Eye />
                </IconButton>
                {!disabled && (
                    <IconButton
                        color="error"
                        data-testid="file-delete-button"
                        size="xsmall"
                        disabled={isDeleting}
                        onClick={handleDelete}
                    >
                        <Trash />
                    </IconButton>
                )}
            </Stack>
        </Stack>
    );
};
